import Indicator from '@components/_shared/Indicator';
import { Box, CircularProgress, Tooltip } from '@mui/material';
import useAlias from '@src/hooks/useAlias';
import { isSuperAdmin } from '@src/services/auth';
import { DataPointWithValue } from '@src/types/DataPoint';
import { ValueGenerationFunction } from '@src/types/Widget';
import { UserState } from '@store/reducers/userData';
import storeConnector from '@store/storeConnector';
import { BooleanInteger } from '@types';
import { formatFloat, isNumberTypeGuard } from '@utils/index_ts';
import { colorForValue } from '@utils/thresholdUtils';
import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';

function getNumericValueForThreshold(
  value: undefined | null | string | number | boolean,
): number | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (isNumberTypeGuard(value)) {
    return value;
  }
  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function parseValue(
  value: undefined | null | string | number | boolean,
  dataType: string,
  scalingFactor: number | null | undefined,
  decimalPrecision: number | null | undefined,
  fullTopic: string,
  invertDisplay: boolean | undefined,
  valueMappings: Record<string | number, string | number | boolean> | undefined,
  alias: (text: string) => string = (text) => text,
  noDataTimeoutValue?: string,
): string | ReactNode {
  if (value === undefined) {
    if (noDataTimeoutValue !== undefined) {
      return noDataTimeoutValue;
    }
    return (
      <Box role='spinner' style={{ display: 'inline-block' }}>
        <CircularProgress size={15} />
      </Box>
    );
  }

  if (value === null) {
    return 'ERR';
  }
  // workaround to not process empty object
  if (
    valueMappings &&
    typeof valueMappings === 'object' &&
    Object.keys(valueMappings).length === 0
  ) {
    valueMappings = undefined;
  }

  if (
    valueMappings &&
    (typeof value === 'string' || typeof value === 'number')
  ) {
    value =
      valueMappings[
        typeof value === 'number' ? JSON.stringify(value) : value
      ] ?? value;
    // force dataType to process the mapped value properly below
    if (typeof value === 'number') {
      dataType = 'integer';
    } else if (typeof value === 'boolean') {
      dataType = 'boolean_integer';
      value = value ? 1 : 0;
    } else {
      dataType = 'string';
    }
  }

  if (dataType === 'integer') {
    if (!isNumberTypeGuard(value)) {
      console.error(
        `ValueLoader: value ${value} is not a number for topic ${fullTopic}`,
      );
      return 'ERR';
    }
    return Math.floor(value).toString();
  }

  if (dataType === 'float') {
    if (!isNumberTypeGuard(value)) {
      console.error(
        `ValueLoader: value ${value} is not a number for topic ${fullTopic}`,
      );
      return 'ERR';
    }
    return formatFloat(value, scalingFactor, decimalPrecision);
  }

  if (dataType === 'boolean_integer') {
    if (!isNumberTypeGuard(value) || ![0, 1].includes(value)) {
      console.error(
        `ValueLoader: value ${value} is not a boolean integer for topic ${fullTopic}`,
      );
      return 'ERR';
    }

    return (
      <Indicator
        value={value as BooleanInteger}
        invertDisplay={invertDisplay}
      />
    );
  }

  return alias(value.toString());
}

function ValueLoaderContent(p: {
  dataPoint: DataPointWithValue;
}): ReactElement {
  const alias = useAlias();
  const {
    value,
    dataType,
    fullTopic,
    scalingFactor,
    decimalPrecision,
    unitOfMeasurement,
    displayDenominator,
    invertDisplay,
    valueMappings,
    thresholdValues,
    noDataTimeout,
  } = p.dataPoint;

  const [hasTimedOut, setHasTimedOut] = useState(false);

  useEffect(() => {
    if (value !== undefined || !noDataTimeout) {
      setHasTimedOut(false);
      return;
    }

    const timer = setTimeout(() => {
      setHasTimedOut(true);
    }, noDataTimeout.timeoutMs);

    return () => clearTimeout(timer);
  }, [value, noDataTimeout]);

  const parsedValue = parseValue(
    value,
    dataType,
    scalingFactor,
    decimalPrecision,
    fullTopic,
    invertDisplay,
    valueMappings,
    alias,
    hasTimedOut && noDataTimeout ? noDataTimeout.valueToShow : undefined,
  );

  const thresholdNumericValue = useMemo(
    () => getNumericValueForThreshold(value),
    [value],
  );

  const getValueColor = useMemo(() => {
    if (thresholdNumericValue !== undefined && thresholdValues) {
      return colorForValue(thresholdNumericValue, thresholdValues);
    }
    return undefined;
  }, [thresholdNumericValue, thresholdValues]);

  return (
    <Box
      sx={{
        ...(getValueColor && { color: getValueColor }),
      }}
      className={'value-loader-box'}
    >
      {parsedValue}
      {unitOfMeasurement && !(hasTimedOut && noDataTimeout) && (
        <span className='value-loader-unit'>{unitOfMeasurement}</span>
      )}
      {displayDenominator && !(hasTimedOut && noDataTimeout) && (
        <span className='value-loader-denominator'>{`/${displayDenominator}`}</span>
      )}
    </Box>
  );
}

interface ValueLoaderProps {
  dataPoint: DataPointWithValue & {
    valueGenerationFunction?: ValueGenerationFunction | null;
  };
  role: UserState['role'];
}

function ValueLoader(p: ValueLoaderProps): ReactElement {
  const title = useMemo(() => {
    if (!isSuperAdmin(p.role)) {
      return '';
    }
    return p.dataPoint.valueGenerationFunction
      ? `Generated value: ${p.dataPoint.valueGenerationFunction?.function} from ${p.dataPoint.valueGenerationFunction?.argumentArray.join(', ')}`
      : p.dataPoint.fullTopic;
  }, [p.role, p.dataPoint.fullTopic, p.dataPoint.valueGenerationFunction]);
  return (
    <Tooltip placement='right' role='tooltip' title={title}>
      <Box>
        <ValueLoaderContent dataPoint={p.dataPoint} />
      </Box>
    </Tooltip>
  );
}

export default storeConnector(ValueLoader, {
  user: ['role'],
});
